import { css } from "@emotion/css";
import React, {
  KeyboardEvent,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from "react";

import { getNavigationElements } from "./helpers";
import { ActionListItem } from "../../components/action-list/ActionListItem";
import { GhostButton } from "../../components/button/GhostButton";
import { Divider } from "../../components/divider/Divider";
import { Text } from "../../foundations/typography/Text";

interface MenuProps {
  children: React.ReactElement | React.ReactElement[];
  triggerRef?: React.RefObject<HTMLButtonElement>;
  triggerButton?: React.ReactElement;
  label?: string;
  headerTitle?: string;
  headerText?: string;
  onClick?: (key?: string, value?: string) => void;
  position?: "right" | "left";
  id?: string;
  testID?: string;
  hideRightIcon?: boolean;
  minWidth?: string;
}

interface ListProps {
  "aria-orientation": "horizontal" | "vertical";
  "aria-label": string;
  "aria-hidden": boolean;
}

/**
 * @deprecated see DropdownMenu
 */
export function LegacyDropdownMenu(props: MenuProps) {
  const {
    label,
    children,
    triggerRef,
    triggerButton = (
      <GhostButton size="small">
        <Text color="text-weak">{label}</Text>
      </GhostButton>
    ),
    headerTitle,
    headerText,
    onClick,
    position = "right",
    id,
    testID = "dropdown-menu",
    hideRightIcon,
  } = props;
  const menuRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>();
  const listProps: ListProps = {
    "aria-orientation": "horizontal",
    "aria-label": label || "",
    "aria-hidden": !isOpen,
  };

  const toggleMenu = async () => await setIsOpen((prev) => !prev);

  // if popover was opened by keyboard - move focus to first item
  const handleOnPress = async (e: any, isKeyboardTriggered?: boolean) => {
    await toggleMenu();
    if (
      (e.pointerType === "keyboard" || isKeyboardTriggered) &&
      menuRef.current
    ) {
      const children = Array.from(menuRef.current.querySelectorAll("li"));
      const firstChild = children[0] as HTMLElement;

      if (firstChild) {
        firstChild.focus();
      }
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.code === "Enter") {
      e.preventDefault();
      handleOnPress(e, true);
    }
  };

  const triggerButtonProps = {
    "aria-haspopup": true,
    "aria-expanded": isOpen,
    id,
    onPress: handleOnPress,
    onKeyDown: handleTriggerKeyDown,
    // conditionally add rightIcon only when using default Cascadia Button
    // triggerRef is used when we pass a custom Button, AccountDropdown as an example
    ...(!triggerRef &&
      !hideRightIcon && { rightIcon: isOpen ? "expand_less" : "expand_more" }),
  };

  const closeMenu = () => setIsOpen(false);

  // Manual check main keys trigger for navigation
  const manageKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const { currentElement, nextElement, prevElement, nextSubElement } =
      getNavigationElements(event.target as HTMLLIElement, menuRef);

    switch (event.code) {
      case "Escape":
        closeMenu();
        break;

      case "Enter":
        currentElement.click();

        if (currentElement.getAttribute("data-key") === "subItem") {
          break;
        }

        closeMenu();
        break;

      case "ArrowDown":
        if (nextElement) {
          nextElement.focus();
        }
        break;

      case "ArrowUp":
        if (prevElement) {
          prevElement.focus();
        }
        break;

      case "ArrowRight":
      case "ArrowLeft":
        if (nextSubElement) {
          nextSubElement.focus();
        }
        break;
    }
  };

  const onMenuClick = (event: React.MouseEvent<HTMLUListElement>) => {
    const target = event.target as HTMLInputElement;
    const key = target.getAttribute("data-key") || "";
    const value = target.textContent || "";
    onClick?.(key, value);
    setIsOpen(false);
  };

  useEffect(() => {
    // close dropdown whenever a click is detected
    // will close the menu when you're clicking outside the menu
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  return (
    <div className={styles.relativeWrapper} data-testid={testID}>
      {cloneElement(triggerButton, triggerButtonProps)}

      {isOpen && (
        <div
          className={css(styles.popover, {
            boxShadow: "var(--shadow-menu)",
            background: "var(--surface-overlay)",
            display: isOpen ? "inline-block" : "none",
            right: position === "right" ? 0 : undefined,
            left: position === "left" ? 0 : undefined,
          })}
        >
          {headerTitle && (
            <>
              <ActionListItem title={headerTitle} children={headerText} />
              <Divider marginVertical="space-1" />
            </>
          )}
          <ul
            role="menu"
            onKeyDown={manageKeyDown}
            ref={menuRef}
            {...listProps}
            tabIndex={-1}
            className={styles.list}
            onClick={onMenuClick}
            id="popover-overlay"
          >
            {children}
          </ul>
        </div>
      )}
    </div>
  );
}

const styles = {
  list: css({
    padding: 0,
    margin: 0,
    listStyleType: "none",
  }),
  relativeWrapper: css({
    position: "relative",
  }),
  popover: css({
    position: "absolute",
    marginTop: "8px",
    zIndex: 99,
    minWidth: "184px",
    borderRadius: "var(--border-radius-2)",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",

    "& li": css({
      marginTop: "var(--space-1)",

      "&:first-child": css({
        marginTop: 0,
      }),
    }),
  }),
};
